import { useState, useCallback } from 'react';
import { chemoTranslations, CHEMO_DEFAULTS } from './chemoTranslations';

export const useChemoFetch = (execute) => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [fetchedRecords, setFetchedRecords] = useState([]);
    const [originalRecords, setOriginalRecords] = useState([]);
    const [KLINE, setKLINE] = useState(null);
    const [isLoadingRecords, setIsLoadingRecords] = useState(false);
    const [topRecords, setTopRecords] = useState(CHEMO_DEFAULTS.TOP_RECORDS);

    const fetchReports = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await execute(
                'GET',
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/chemo/getReports`
            );
            setReports(response?.reports || []);
        } catch (err) {
            setReports([]);
        } finally {
            setLoading(false);
        }
    }, [execute]);

    const fetchHospitalReport = useCallback(async (hospitalName, top = topRecords) => {
        setIsLoadingRecords(true);
        setError(null);

        try {
            const queryParams = new URLSearchParams({
                hospitalName: hospitalName,
                top: top.toString()
            });

            const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/chemo/hospital-report?${queryParams.toString()}`;

            const response = await execute('GET', url);

            const reports = Array.isArray(response) ? response : [response];

            setFetchedRecords(reports);
            setOriginalRecords(JSON.parse(JSON.stringify(reports)));

            return { success: true, data: response, records: reports };
        } catch (err) {
            setError(chemoTranslations.errors.loadingError);
            return { success: false, error: err.message };
        } finally {
            setIsLoadingRecords(false);
        }
    }, [execute, topRecords]);

    const fetchReportsByDate = useCallback(async (startDate, endDate) => {
        setIsLoadingRecords(true);
        setError(null);

        try {
            const queryParams = new URLSearchParams({
                startDate: startDate,
                endDate: endDate
            });

            const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/chemo/reports-by-date?${queryParams.toString()}`;

            const response = await execute('GET', url);

            const reports = Array.isArray(response) ? response : [response];

            setFetchedRecords(reports);
            setOriginalRecords(JSON.parse(JSON.stringify(reports)));

            return { success: true, data: response, records: reports };
        } catch (err) {
            setError(chemoTranslations.errors.loadingError);
            return { success: false, error: err.message };
        } finally {
            setIsLoadingRecords(false);
        }
    }, [execute]);

    const fetchChemoRecords = useCallback(async (params) => {
        setIsLoadingRecords(true);
        setError(null);

        try {
            const queryParams = new URLSearchParams({
                hospitalName: params.hospital,
                startDate: params.startDate,
                endDate: params.endDate
            });

            const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/chemo/records?${queryParams.toString()}`;

            const response = await execute('GET', url);
console.log(url);

            const recordsWithEditFields = (response?.NEO_PARTDUEDATE_LINE_SUBFORM || []).map(record => ({
                ...record,
                APPROVED: record.APPROVED || false,
                NOTAPPROVED: record.NOTAPPROVED || false,
                FINISH: record.FINISH || false,
                TODUEDATE: record.TODUEDATE || ''
            }));

            setKLINE(response?.KLINE || null);
            setFetchedRecords(recordsWithEditFields);
            setOriginalRecords(JSON.parse(JSON.stringify(recordsWithEditFields)));

            return { success: true, data: response, records: recordsWithEditFields };
        } catch (err) {
            setError(chemoTranslations.errors.loadingError);
            return { success: false, error: err.message };
        } finally {
            setIsLoadingRecords(false);
        }
    }, [execute]);

    const handleTopChange = useCallback((newTop) => {
        setTopRecords(newTop);
    }, []);

    const resetRecords = useCallback(() => {
        setFetchedRecords([]);
        setOriginalRecords([]);
    }, []);

    return {
        reports,
        loading,
        setLoading,
        error,
        setError,
        fetchedRecords,
        setFetchedRecords,
        originalRecords,
        setOriginalRecords,
        KLINE,
        isLoadingRecords,
        topRecords,
        fetchReports,
        fetchHospitalReport,
        fetchReportsByDate,
        fetchChemoRecords,
        handleTopChange,
        resetRecords
    };
};
